import { Fragment, useEffect, useRef, useState } from 'react';
import Typography from '../typography/typography.component';
import Image from 'next/image';

interface TabData {
    title: string;
    content: React.ReactElement<any>;
    activeIcon: string;
    inactiveIcon: string;
    activeBgColor?: string;
    activeBorderColor?: string;
    activeTextColor?: string;
}

interface TabsProps {
    tabsData: TabData[];
    inactiveBgColor?: string;
    inactiveTextColor?: string;
    inactiveBorderColor?: string;
    textClass?: string;
    tabClass?: string;
    tabContainerClass?: string;
    tabContentContainerClass?: string;
    initialActiveTab?: number;
}
const TabContent = ({
    tabContentContainerClass,
    content,
}: {
    tabContentContainerClass?: string;
    content: React.ReactElement<any>;
}) => {
    return (
        <div
            className={`${tabContentContainerClass} `}
            onTouchMove={e => {
                e.stopPropagation();
            }}
        >
            {content}
        </div>
    );
};
function Tabs({
    tabsData,
    inactiveBgColor = 'bg-white',
    inactiveTextColor = 'text-gray-600',
    inactiveBorderColor = 'border-gray-200',
    textClass,
    tabClass,
    tabContainerClass,
    initialActiveTab = 0,
    tabContentContainerClass,
}: TabsProps) {
    const [activeTab, setActiveTab] = useState<number>(
        initialActiveTab >= tabsData.length ? 0 : initialActiveTab
    );
    const tabRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        // Create timeout IDs

        const tabTimeoutId = setTimeout(() => {
            if (tabRef?.current) {
                tabRef.current.scrollIntoView({
                    behavior: 'smooth',
                    inline: activeTab === 0 ? 'end' : 'start',
                });
            }
        }, 400);

        // Cleanup function to clear timeouts
        return () => {
            clearTimeout(tabTimeoutId);
        };
    }, [activeTab]); // Dependency array

    return (
        <div className="w-full h-full relative ">
            <div
                className={`flex justify-between gap-2 h-max overflow-visible ${tabContainerClass}`}
            >
                {tabsData.map((tab, index) => {
                    const isActiveTab = activeTab === index;
                    return (
                        <div
                            className="w-full  h-max"
                            key={`${tab.title} tab-title`}
                        >
                            <div
                                className={`w-full h-10 py-2 px-4 rounded-[100px] cursor-pointer flex items-center justify-center border relative ${
                                    isActiveTab
                                        ? tab.activeBorderColor
                                        : inactiveBorderColor
                                }
                        ${isActiveTab ? tab.activeBgColor : inactiveBgColor}
                         ${tabClass}`}
                                onClick={() => setActiveTab(index)}
                            >
                                {/* for scrolling animation */}
                                <div
                                    ref={tabRef}
                                    className="absolute en:left-0 ar:right-0  w-1 h-full"
                                />
                                {/*  */}
                                <div className="flex items-center justify-center gap-1.5 font-semibold w-max">
                                    <Image
                                        className="w-5 h-5 ar:mb-0.5"
                                        src={
                                            isActiveTab
                                                ? tab.activeIcon
                                                : tab.inactiveIcon
                                        }
                                        alt={`${tab.title} icon`}
                                    />
                                    <Typography
                                        className={`${textClass} ${
                                            isActiveTab
                                                ? tab.activeTextColor
                                                : inactiveTextColor
                                        }`}
                                        variant={
                                            isActiveTab
                                                ? 'captionSemiBold'
                                                : 'captionMedium'
                                        }
                                    >
                                        {tab.title}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Tab Content */}

            {tabsData.map((tab, index) => {
                return (
                    <Fragment key={index}>
                        {activeTab === index && (
                            <TabContent
                                tabContentContainerClass={
                                    tabContentContainerClass
                                }
                                content={tab.content}
                            />
                        )}
                    </Fragment>
                );
            })}
        </div>
    );
}
export default Tabs;
